import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WebStorageService } from './web-storage.service';

export abstract class BaseService {
  constructor(private httpClient: HttpClient, protected webStorageService: WebStorageService) {}

  private getHeaders(httpHeaders: HttpHeaders = null): HttpHeaders {
    const loggedInUser = this.webStorageService.getCurrentUserToken();
    const clientConfig = this.webStorageService.getClientConfig();

    let headers = httpHeaders ?? new HttpHeaders();

    if (loggedInUser) {
      headers = headers.set('loggedInUsername', loggedInUser.userFullName);
    }

    const auditLogEnabled = clientConfig?.isAuditLogEnabled ?? false;
    headers = headers.set('isAuditLogEnable', auditLogEnabled.toString());

    return headers;
  }

  private handleRequest<T>(method: string, url: string, body: any = null, httpHeaders: HttpHeaders = null, forceRefresh: boolean = false): Observable<T> {
    let headers = this.getHeaders(httpHeaders);

    if (forceRefresh) {
      headers = headers.set('forceRefresh', 'true');
    }

    const apiUrl = this.getApiUrl(url);

    switch (method) {
      case 'GET':
        return this.httpClient.get<T>(apiUrl, { headers });
      case 'POST':
        return this.httpClient.post<T>(apiUrl, body, { headers });
      case 'DELETE':
        return this.httpClient.delete<T>(apiUrl, { headers });
      default:
        throw new Error('Unsupported HTTP method');
    }
  }

  protected get<T>(url: string, httpHeaders: HttpHeaders = null): Observable<T> {
    return this.handleRequest<T>('GET', url, null, httpHeaders);
  }

  protected getRefresh<T>(url: string, httpHeaders: HttpHeaders = null): Observable<T> {
    return this.handleRequest<T>('GET', url, null, httpHeaders, true);
  }

  protected post<T>(url: string, body: any, httpHeaders: HttpHeaders = null): Observable<T> {
    return this.handleRequest<T>('POST', url, body, httpHeaders);
  }

  protected delete<T>(url: string, httpHeaders: HttpHeaders = null): Observable<T> {
    return this.handleRequest<T>('DELETE', url, null, httpHeaders);
  }

  protected postRefresh<T>(url: string, body: any, httpHeaders: HttpHeaders = null): Observable<T> {
    return this.handleRequest<T>('POST', url, body, httpHeaders, true);
  }

  protected postCustomUrl<T>(url: string, body: any, httpHeaders: HttpHeaders = null): Observable<T> {
    const headers = this.getHeaders(httpHeaders);
    return this.httpClient.post<T>(url, body, { headers });
  }

  protected downloadBlobFile(url: string, body: any, httpHeaders: HttpHeaders = null): Observable<Blob> {
    const headers = this.getHeaders(httpHeaders);
    return this.httpClient.post(this.getApiUrl(url), body, { headers, responseType: 'blob' });
  }

  protected downloadTextFile(url: string, body: any, httpHeaders: HttpHeaders = null): Observable<string> {
    const headers = this.getHeaders(httpHeaders);
    return this.httpClient.post(this.getApiUrl(url), body, { headers, responseType: 'text' });
  }

  protected downloadCsvFile(url: string, body: any = null, httpHeaders: HttpHeaders = null): Observable<Blob> {
    const headers = this.getHeaders(httpHeaders);
    return this.httpClient.post(this.getApiUrl(url), body, { headers, responseType: 'blob' });
  }

  protected put<T>(url: string, body: any, httpHeaders: HttpHeaders = null): Observable<T> {
    return this.handleRequest<T>('PUT', url, body, httpHeaders);
  }

  protected getBlob(url: string, httpHeaders: HttpHeaders = null): Observable<Blob> {
    const headers = this.getHeaders(httpHeaders);
    return this.httpClient.get(this.getApiUrl(url), { headers, responseType: 'blob' });
  }

  private getApiUrl(url: string): string {
    return environment.apiBaseUrl + url;
  }
}
